import React, { FC, useCallback, useEffect, useRef } from 'react';
import { loadFontsForDesign } from 'src/components/widgets/preview3D/loaders/designFontLoader';
import { Load3DSceneViewer } from 'src/components/widgets/preview3D/loaders/load3DScene';
import { LoadPreview3D } from 'src/components/widgets/preview3D/loaders/loadPreview3D';
import usePreview3DStore, { Preview3DContext } from 'src/components/widgets/preview3D/store';
import { T, toGlobalID } from 'src/shared/helpers';
import styled from 'styled-components';
import config from '../../../../../shared/config';
import { useAIStudioProduct } from '../context/productContext';
import { useViewer3DCapture } from '../context/viewer3DContext';

// ============================================
// Styled Components
// ============================================

export const Viewer3DContainer = styled.div`
    width: 100%;
    max-width: 400px;
    height: 350px;
    border-radius: 12px;
    overflow: hidden;
    border: 2px solid #e5e7eb;
    background: #f5f5f5;
`;

const Viewer3DInner = styled.div`
    width: 100%;
    height: 100%;
    position: relative;
    background: #f5f5f5;
`;

const Viewer3DLoading = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    background: #f5f5f5;
    font-size: 14px;
    color: #78909c;
`;

// ============================================
// VariantSelector
// ============================================

/**
 * Selects a variant/color in the 3D scene.
 * Must be rendered inside the Preview3DContext after Load3DScene.
 */
const VariantSelector: FC<{ variantId: number }> = ({ variantId }) => {
    const sceneViewer = usePreview3DStore((x) => x.sceneViewer);
    const sceneManager = usePreview3DStore((x) => x.sceneManager);
    const zkProduct = usePreview3DStore((x) => x.zkProduct);
    const zkDesign = usePreview3DStore((x) => x.zkDesign);
    const zkDesignRef = useRef(zkDesign);
    zkDesignRef.current = zkDesign;
    const appliedRef = useRef(false);

    useEffect(() => {
        if (!sceneViewer || !sceneManager || !zkProduct || appliedRef.current) return;

        const selectVariant = async () => {
            try {
                appliedRef.current = true;

                const color = zkProduct.get('colors').find((x: any) => x.get('colorID') === variantId);
                if (!color) {
                    console.warn('Variant not found:', variantId);
                    return;
                }

                const attributeSelection = sceneManager.getSelectedAttributeOption(color);

                if (attributeSelection && attributeSelection.attributeGuid && attributeSelection.optionGuid) {
                    const zkAttribute = zkProduct.get('attributes').find((x: any) => x.get('attributeGuid') === attributeSelection.attributeGuid);

                    if (zkAttribute) {
                        const zkOption = zkAttribute.get('options').find((x: any) => x.get('optionGuid') === attributeSelection.optionGuid);

                        if (zkOption) {
                            const actions = zkOption.get('actions').sortBy((item: any) => item.get('execOrder'));

                            await sceneViewer.executeActionsAsync(null, null, actions);
                            sceneViewer.adjustCamera(false);

                            const design = zkDesignRef.current;
                            if (design) {
                                sceneViewer.resetDesign();
                                await sceneViewer.renderDesignAsync(design);
                            }
                        }
                    }
                }
            } catch (err) {
                console.error('Error selecting variant:', err);
            }
        };

        selectVariant();
    }, [sceneViewer, sceneManager, zkProduct, variantId]);

    return null;
};

// ============================================
// DesignLoader
// ============================================

/**
 * Loads a design by docId and applies it to the 3D scene.
 * Must be rendered inside the Preview3DContext after Load3DScene.
 */
const DesignLoader: FC<{ docId: string }> = ({ docId }) => {
    const sceneViewer = usePreview3DStore((x) => x.sceneViewer);
    const zkProduct = usePreview3DStore((x) => x.zkProduct);
    const setZkDesign = usePreview3DStore((x) => x.setZkDesign);
    const loadedRef = useRef(false);

    useEffect(() => {
        if (!sceneViewer || !zkProduct || !docId || loadedRef.current) return;

        const loadDesign = async () => {
            try {
                loadedRef.current = true;

                const $ = (window as any).jQuery;
                if ($) {
                    $.ajaxSetup({ cache: true, headers: { Authorization: 'Bearer ' + config.token } });
                    (window as any).$ = $;
                    (window as any).jQuery = $;
                }

                const design = new MPlaza.Design({ docID: docId });
                design.setModel(zkProduct);

                await new Promise<void>((resolve, reject) => {
                    design.fetch({
                        success: () => resolve(),
                        error: (response: unknown) => {
                            console.error('Failed to fetch design:', response);
                            reject(new Error('Failed to fetch design'));
                        },
                        data: {
                            template: false,
                            frombackoffice: true,
                            fromOrderDesignEditor: false
                        }
                    });
                });

                await loadFontsForDesign(design);
                setZkDesign(design);
                sceneViewer.resetDesign();
                await sceneViewer.renderDesignAsync(design);
            } catch (err) {
                console.error('Error loading design:', err);
            }
        };

        loadDesign();
    }, [sceneViewer, zkProduct, docId, setZkDesign]);

    return null;
};

// ============================================
// Viewer3DCanvas
// ============================================

/**
 * Inner 3D viewer component that renders the SceneEditor canvas.
 */
const Viewer3DCanvas: FC = () => {
    const containerRef = useRef<HTMLDivElement>(null);
    const sceneViewer = usePreview3DStore((x) => x.sceneViewer);
    const viewer3DContext = useViewer3DCapture();
    const { docId, variantId } = useAIStudioProduct();

    const captureScreenshot = useCallback(async (): Promise<string | null> => {
        if (!sceneViewer) {
            console.error('SceneViewer not available for screenshot capture');
            return null;
        }
        try {
            const screenshot = await sceneViewer.getSceneContentScreenshotAsync(1024, 1024, 0, undefined, false, undefined, '#FFFFFF');
            return screenshot || null;
        } catch (error) {
            console.error('Screenshot capture failed:', error);
            return null;
        }
    }, [sceneViewer]);

    useEffect(() => {
        if (sceneViewer && viewer3DContext) {
            viewer3DContext.registerCapture(captureScreenshot);
        }
    }, [sceneViewer, viewer3DContext, captureScreenshot]);

    useEffect(() => {
        if (!containerRef.current || !sceneViewer) return;
        sceneViewer.appendTo(containerRef.current);
        sceneViewer.updateView(true, true);
        sceneViewer.isSelectionMode = false;

        return () => {
            sceneViewer?.detach();
        };
    }, [sceneViewer]);

    return (
        <>
            <Viewer3DInner ref={containerRef} />
            {variantId && <VariantSelector variantId={variantId} />}
            {docId && <DesignLoader docId={docId} />}
        </>
    );
};

// ============================================
// Load3DSceneWithComposition
// ============================================

/**
 * Wrapper that provides compositionDocId from context to Load3DScene.
 */
const Load3DSceneWithComposition: FC<{ children: React.ReactNode; fallback: React.ReactNode }> = ({ children, fallback }) => {
    const { compositionDocId, variantId } = useAIStudioProduct();
    return (
        <Load3DSceneViewer fallback={fallback} onProgress={() => {}} compositionDocId={compositionDocId} variantId={variantId}>
            {children}
        </Load3DSceneViewer>
    );
};

// ============================================
// ProductViewer3D (public component)
// ============================================

interface ProductViewer3DProps {
    productId: number;
}

/**
 * Complete 3D product viewer with scene loading, design, and variant support.
 */
export const ProductViewer3D: FC<ProductViewer3DProps> = ({ productId }) => {
    return (
        <Viewer3DContainer>
            <Preview3DContext>
                <LoadPreview3D
                    productId={toGlobalID(productId, 'Model')}
                    fallback={<Viewer3DLoading>{T._('Loading 3D viewer...', 'Admin')}</Viewer3DLoading>}
                    onSceneLoaded={() => {}}
                >
                    <Load3DSceneWithComposition fallback={<Viewer3DLoading>{T._('Loading 3D scene...', 'Admin')}</Viewer3DLoading>}>
                        <Viewer3DCanvas />
                    </Load3DSceneWithComposition>
                </LoadPreview3D>
            </Preview3DContext>
        </Viewer3DContainer>
    );
};
